import { z } from "zod";
import { nameSchema } from "./name.schema";
import { descriptionSchema } from "./description.schema";
import { numberSchema } from "./networkLimit.schema";
import { numberOrStringSchema } from "./numberOrStringSchema";

export const PolicyPageSchema = z.object({
  policyName: nameSchema,
  policyId: numberOrStringSchema,
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required"),
});

export const policyDateSchema = PolicyPageSchema.refine(
  (data) => {
    if (!data.startDate || !data.endDate) return true;
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    return isNaN(start.getTime()) || isNaN(end.getTime()) ? true : end >= start;
  },
  {
    message: "End date must be after or equal to start date",
    path: ["endDate"],
  }
);

export const groupSchema = z.object({
  name: nameSchema,
  description: descriptionSchema,
  limitOfApproval: numberSchema,
  tpaId: numberOrStringSchema,
  payerId: numberOrStringSchema,
  code: numberOrStringSchema,
  licenseNumber: numberOrStringSchema,
  policyName: nameSchema,
  policyId: numberOrStringSchema,
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  ruleName: nameSchema,
});

export const networkPageSchema = z.object({
  name: nameSchema,
  limitOfApproval: numberSchema,
});

export const descriptionPageSchema = z.object({
  name: nameSchema,
});

export const TpaPageSchema = z.object({
  name: nameSchema,
  tpaId: numberOrStringSchema,
});

export const PayerPageSchema = z.object({
  name: nameSchema,
  payerId: numberOrStringSchema,
});

export const SpecialtyPageSchema = z.object({
  name: nameSchema,
  code: numberOrStringSchema,
});

export const DoctorPageSchema = z.object({
  name: nameSchema,
  licenseNumber: numberOrStringSchema,

});

export const IcdNotCoveredPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
});
export const IcdNotAllowedPrincipalDiagnosisPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
});
export const ActivityNotCoveredPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
});
export const ActivitySupportingDocsSubmissionPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
});
export const ActivityPreApprovalsPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
});
export const ActivityBundlePageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
});
export const ActivitiesNotAllowedPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
});
export const ActivityNotAllowedICDPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
});
export const ActivitiesAllowedPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
});
export const ActivitiesFreqPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.number().default(1),
  quantity: z.union([z.number(), z.string()])
    .refine(val => val !== "", "Quantity is required")
    .transform(val => Number(val)),

  periodUnit: z.string().min(1, "Please select a valid period unit"),

  periodFrequency: z.union([z.number(), z.string()])
    .refine(val => val !== "", "Frequency is required")
    .transform(val => Number(val)),

  recurringLimitation: z.union([z.number(), z.string()])
    .refine(val => val !== "", "Recurring limitation is required")
    .transform(val => Number(val)),
});
export const ActivityAgeGenderPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.coerce.number().min(0).default(1),
  genderValidation: z.boolean().optional(),
  gender: z.enum(["MALE", "FEMALE", "BOTH"]),
  ageFrom: z.preprocess(
    (val) => (val === "" || val === undefined ? null : val),
    z.coerce.number().min(0).nullable().optional()
  ),
  ageTo: z.preprocess(
    (val) => (val === "" || val === undefined ? null : val),
    z.coerce.number().min(0).nullable().optional()
  ),
  ageUnit: z.enum(["YEARS", "MONTHS", "DAYS"]),
}).superRefine((data, ctx) => {
  if (data.genderValidation === true && (data.gender === "BOTH" || !data.gender)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Gender Must Be Male Or Female When Validation Is Active",
      path: ["gender"],
    });
  }
});
export const ICDAgeGenderPageSchema = z.object({
  ruleName: nameSchema,
  priority: z.coerce.number().min(0).default(1),
  genderValidation: z.boolean().optional(),
  gender: z.enum(["MALE", "FEMALE", "BOTH"]),
  ageFrom: z.preprocess(
    (val) => (val === "" || val === undefined ? null : val),
    z.coerce.number().min(0).nullable().optional()
  ),
  ageTo: z.preprocess(
    (val) => (val === "" || val === undefined ? null : val),
    z.coerce.number().min(0).nullable().optional()
  ),
  ageUnit: z.enum(["YEARS", "MONTHS", "DAYS"]),
}).superRefine((data, ctx) => {
  if (data.genderValidation === true && (data.gender === "BOTH" || !data.gender)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Gender Must Be Male Or Female When Validation Is Active",
      path: ["gender"],
    });
  }
});

export type GroupValues = z.infer<typeof groupSchema>;
export type PolicyPageValues = z.infer<typeof PolicyPageSchema>;
